import { useState } from 'react';
import { toast } from 'react-toastify';
import './Support.css';
import FilterDropdown from './FilterDropdown';

const subcategoryOptions = {
  'Fuel Issue': ['Wrong Fuel Alert', 'Fuel Data not Loading', 'No Theft Alerts', 'Calibration Table Issue', 'Fuel Tank Capacity Updation', 'Incorrect Fuel Recording'],
  'Alert/Reports': ['Payment Issue', 'Invoice Request', 'Refund Request'],
  Sensor: ['App Crash', 'Login Issue'],
  'General Query': ['Trips', 'Tyre Module', 'Account Updation', 'Vehicle Transfer', 'User Add/ Delete/Modify', 'Vehicle Add/ Delete/Modify', 'OTP Issue', 'Others', 'Device Add/Delete'],
  'Vehicle / Devices': ['Dashacam', 'Frequent Offline', 'Device Offline', 'Immobilization Issue', 'Incorrect Location', 'Vehicle/Device Battery Issue', 'Incorrect Moving/Stopped Location', 'Incorrect Odometer', 'Installation/ Re-Installation'],
  'Device Purchase/ Device Renewal': ['New Device Purchase', 'Renewal', 'Modify Subscription'],
};

const API_BASE = process.env.REACT_APP_SUPPORT_API_BASE;

export default function TicketForm({ user, onCancel, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    vehicleId: '',
    description: '',
    attachment: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category') {
      setFormData((current) => ({ ...current, category: value, subcategory: '' }));
      return;
    }
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((current) => ({ ...current, attachment: e.target.files[0] || null }));
  };

  const handleReset = () => {
    setFormData({ category: '', subcategory: '', vehicleId: '', description: '', attachment: null });
    setStep(1);
  };

  const handleNext = () => {
    if (!formData.category || !formData.subcategory) {
      toast.warning('Please select a category and subcategory.');
      return;
    }
    setStep(2);
  };

  const handleBack = () => setStep(1);

  const handleSubmit = async () => {
    if (!formData.vehicleId.trim() || !formData.description.trim()) {
      toast.warning('Vehicle ID and description are required.');
      return;
    }

    setLoading(true);
    let uploadedFileKey = null;
    try {
      const currentUser = user || localStorage.getItem('authUser') || '';
      if (!currentUser) {
        throw new Error('Unable to identify logged-in user. Please login again.');
      }

      if (formData.attachment) {
        const uploadUrlResponse = await fetch(`${API_BASE}/tickets/upload-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: formData.attachment.name,
            fileType: formData.attachment.type,
            userId: currentUser,
          }),
        });
        if (!uploadUrlResponse.ok) {
          const errorData = await uploadUrlResponse.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to get upload URL.');
        }
        const uploadData = await uploadUrlResponse.json();
        const uploadUrl = uploadData.uploadUrl || uploadData.signedUrl;
        uploadedFileKey = uploadData.fileKey || uploadData.key;
        if (!uploadUrl) {
          throw new Error('Upload URL was not returned by the server.');
        }
        const s3Response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': formData.attachment.type },
          body: formData.attachment,
        });
        if (!s3Response.ok) {
          throw new Error('Failed to upload the attachment.');
        }
      }

      const payload = {
        userId: currentUser,
        category: formData.category,
        subcategory: formData.subcategory,
        vehicleId: formData.vehicleId,
        description: formData.description,
        status: 'Open',
        attachmentKey: uploadedFileKey,
      };

      const ticketResponse = await fetch(`${API_BASE}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!ticketResponse.ok) {
        const errorData = await ticketResponse.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to create the ticket.');
      }

      toast.success('Ticket created successfully.');
      handleReset();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('[TicketForm] Submit failed', err);
      toast.error(err.message || 'Unable to submit ticket.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ticket-form-card">
      <div className="support-card-header">
        <div>
          <h3>{step === 1 ? 'Ticket Details' : 'Submit Ticket'}</h3>
          
        </div>
        <div className="ticket-form-actions">
          <button className="support-button tertiary" onClick={onCancel} type="button">Cancel</button>
          {step === 2 && (
            <button className="support-button secondary" onClick={handleBack} type="button">Back</button>
          )}
        </div>
      </div>

      <div className="ticket-stepper">
        <div className={`ticket-step ${step === 1 ? 'active' : ''}`}>1. Category</div>
        <div className={`ticket-step ${step === 2 ? 'active' : ''}`}>2. Submit</div>
      </div>

      {step === 1 && (
        <div className="support-form">
    <label>Category</label>
<FilterDropdown
  value={formData.category}
  onChange={(value) => setFormData((current) => ({ ...current, category: value, subcategory: '' }))}
  placeholder="Select a category"
  options={Object.keys(subcategoryOptions).map((category) => ({ value: category, label: category }))}
/>

<label>Sub-Category</label>
<FilterDropdown
  value={formData.subcategory}
  onChange={(value) => setFormData((current) => ({ ...current, subcategory: value }))}
  placeholder="Select a sub-category"
  disabled={!formData.category}
  options={(subcategoryOptions[formData.category] || []).map((sub) => ({ value: sub, label: sub }))}
/>

          <button className="support-button primary" onClick={handleNext} type="button">Continue</button>
        </div>
      )}

      {step === 2 && (
        <div className="support-form">
          <label>Vehicle ID</label>
          <input
            type="text"
            name="vehicleId"
            placeholder="Enter Vehicle ID (e.g. TRK-001)"
            value={formData.vehicleId}
            onChange={handleChange}
          />

          <label>Description</label>
          <textarea
            name="description"
            rows="5"
            placeholder="Describe the issue in detail..."
            value={formData.description}
            onChange={handleChange}
          />

          <label>Attachment</label>
          <input
            type="file"
            name="attachment"
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png,.csv,.pdf,.xlsx"
          />

          <div className="support-form-footer">
            <button className="support-button primary" onClick={handleSubmit} type="button" disabled={loading}>
              {loading ? 'Submitting...' : 'Raise Ticket'}
            </button>
            <button className="support-button tertiary" onClick={handleReset} type="button">Reset</button>
          </div>
        </div>
      )}
    </div>
  );
}
